import { Document } from '../types';
import { api, handleServiceError } from './api';

// ====================================================================
// TIPOS
// ====================================================================

// Classificação do documento — controla em quais análises de IA ele pode ser usado:
// normativo = único tipo visível em Enquadramento, Documental (exigências) e Mérito.
// anexo_proposta = único tipo usado na verificação/avaliação documental.
export type DocumentType = 'normativo' | 'proposta' | 'anexo_proposta' | 'identificacao_parte' | 'outro';

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  normativo: 'Arquivo Normativo',
  proposta: 'Arquivo para Proposta',
  anexo_proposta: 'Arquivo Anexo Proposta',
  identificacao_parte: 'Documento de identificação da parte',
  outro: 'Arquivo Outro',
};

const DOCUMENT_TYPE_DESCRIPTIONS: Partial<Record<DocumentType, string>> = {
  normativo: 'Edital, Anexos, Regramentos, etc.',
  proposta: 'Esboços, Informações, etc.',
  anexo_proposta: 'Exigência Normativa',
};

// As opções são derivadas dos rótulos para que o formulário e a listagem
// sempre apresentem a mesma nomenclatura (inclusive para novos tipos).
export const DOCUMENT_TYPE_OPTIONS: { value: DocumentType; label: string; description: string }[] =
  (Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]).map((value) => ({
    value,
    label: DOCUMENT_TYPE_LABELS[value],
    description: DOCUMENT_TYPE_DESCRIPTIONS[value] || '',
  }));

export interface DocumentCreate {
  proposal_id: number;
  title: string;
  description: string;
  context?: string;
  document_type: DocumentType;
}

export interface DocumentUpdate {
  title?: string;
  description?: string;
  context?: string;
  document_type?: DocumentType;
}

export interface DocumentFromURL {
  proposal_id: number;
  title: string;
  description: string;
  url: string;
  document_type: DocumentType;
  crawl_sublinks?: boolean;
}

// ====================================================================
// DOCUMENT SERVICE
// ====================================================================

export const documentService = {
  /**
   * Busca todos os documentos de uma proposta. Opcionalmente filtra por document_type
   * (ex: só os Arquivos Normativos) usando o filtro suportado pelo backend.
   */
  getByProposal: async (proposalId: number, documentType?: DocumentType): Promise<Document[]> => {
    try {
      const response = await api.get(`/documents/by-proposal/${proposalId}`, {
        params: documentType ? { document_type: documentType } : undefined,
      });

      // Validação robusta da resposta
      if (!response?.data) {
        console.warn('DocumentService: Resposta vazia da API');
        return [];
      }
      
      // Se for array direto, retorna
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Se for objeto com propriedade documents, extrai o array
      if (typeof response.data === 'object' && Array.isArray(response.data.documents)) {
        return response.data.documents;
      }
      
      // Log para debugging de estruturas inesperadas
      console.warn('DocumentService: Estrutura de resposta inesperada:', response.data);
      return [];
      
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'get_by_proposal' });
      console.error('DocumentService: Erro ao buscar documentos:', error);
      return []; // Garantir que sempre retorne array em caso de erro
    }
  },

  /**
   * Busca um documento específico por ID
   */
  getById: async (documentId: number): Promise<Document | null> => {
    try {
      const response = await api.get(`/documents/${documentId}`);
      return response?.data || null;
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'get_by_id' });
      return null;
    }
  },

  /**
   * Cria um novo documento fazendo upload de arquivo
   * IMPORTANTE: Usa FormData para enviar arquivo + metadados
   *
   * @param proposalId - ID da proposta
   * @param file - Arquivo para upload
   * @param title - Título do documento (opcional, usa nome do arquivo se não fornecido)
   * @param description - Descrição do documento
   * @param context - Contexto inicial (opcional)
   */
  create: async (
    proposalId: number,
    file: File,
    title?: string,
    description?: string,
    context?: string,
    documentType: DocumentType = 'outro'
  ): Promise<Document | null> => {
    try {
      const formData = new FormData();

      // Adiciona o arquivo
      formData.append('file', file);

      // Adiciona os metadados como JSON (mesma estrutura do Streamlit)
      const metadata = {
        proposal_id: proposalId,
        title: title || file.name,
        description: description || '',
        context: context || '',
        document_type: documentType,
      };
      formData.append('metadata_json', JSON.stringify(metadata));

      const response = await api.post('/documents/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response?.data || null;
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'create_' });
      return null;
    }
  },

  /**
   * Cria um documento a partir de uma URL
   * O backend faz web scraping da URL
   */
  createFromURL: async (data: DocumentFromURL): Promise<Document | null> => {
    try {
      const response = await api.post('/documents/from-url', data);
      return response?.data || null;
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'create_from_url' });
      return null;
    }
  },

  /**
   * Atualiza um documento existente
   */
  update: async (documentId: number, data: DocumentUpdate): Promise<Document | null> => {
    try {
      const response = await api.patch(`/documents/${documentId}`, data);
      return response?.data || null;
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'update_' });
      return null;
    }
  },

  /**
   * Busca o arquivo binário original do documento (para preview/download)
   */
  getFileBlob: async (documentId: number): Promise<Blob> => {
    try {
      const response = await api.get(`/documents/${documentId}/file`, { responseType: 'blob' });
      return response.data;
    } catch (error: any) {
      // Com responseType 'blob', o axios devolve o corpo de erro (ex: 404 JSON com "detail")
      // como um Blob em vez de objeto parseado — sem isso, getErrorMessage nunca acha o "detail"
      // real do backend (ex: "Arquivo não encontrado") e cai sempre na mensagem genérica.
      const data = error?.response?.data;
      if (data instanceof Blob && data.type.includes('json')) {
        try {
          error.response.data = JSON.parse(await data.text());
        } catch {
          // Não era JSON parseável — mantém o Blob original
        }
      }
      return handleServiceError(error, { feature: 'documents', action: 'get_file_blob' });
    }
  },

  /**
   * Deleta um documento
   */
  delete: async (documentId: number): Promise<void> => {
    try {
      await api.delete(`/documents/${documentId}`);
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'delete_' });
    }
  },

  /**
   * Sincroniza is_indexed dos documentos com o estado real do Vector Store.
   * Não faz upload — apenas lê o VS e corrige flags desatualizadas (operação barata).
   */
  syncIndexStatus: async (proposalId: number): Promise<{ synced: number; skipped: number }> => {
    try {
      const response = await api.post(`/documents/by-proposal/${proposalId}/sync-index`);
      return response?.data ?? { synced: 0, skipped: 0 };
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'sync_index_status' });
      return { synced: 0, skipped: 0 };
    }
  },

  /**
   * Força a reindexação de um documento específico (reseta flag e agenda upload).
   */
  reindex: async (documentId: number): Promise<void> => {
    try {
      await api.post(`/documents/${documentId}/reindex`);
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'reindex' });
      throw error;
    }
  },

  /**
   * Reordena documentos de uma proposta
   */
  reorder: async (proposalId: number, documentIds: number[]): Promise<void> => {
    try {
      await api.post('/documents/reorder', {
        proposal_id: proposalId,
        document_ids: documentIds,
      });
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'reorder' });
    }
  },

  // ====================================================================
  // HELPERS DE UPLOAD COM PROGRESSO
  // ====================================================================

  /**
   * Upload de documento com callback de progresso
   *
   * @param proposalId - ID da proposta
   * @param file - Arquivo para upload
   * @param title - Título do documento
   * @param description - Descrição do documento
   * @param onProgress - Callback para progresso do upload (0-100)
   * @param context - Contexto inicial (opcional)
   */
  uploadWithProgress: async (
    proposalId: number,
    file: File,
    title: string,
    description: string,
    onProgress?: (progress: number) => void,
    context?: string,
    documentType: DocumentType = 'outro'
  ): Promise<Document | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const metadata = {
        proposal_id: proposalId,
        title: title || file.name,
        description: description || '',
        context: context || '',
        document_type: documentType,
      };
      formData.append('metadata_json', JSON.stringify(metadata));

      const response = await api.post('/documents/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });

      return response?.data || null;
    } catch (error) {
      handleServiceError(error, { feature: 'documents', action: 'upload_with_progress' });
      return null;
    }
  },

  getContentByProposal: async (proposalId: number, maxCharsPerDoc = 3000): Promise<DocumentContent[]> => {
    try {
      const response = await api.get(`/documents/by-proposal/${proposalId}/content`, {
        params: { max_chars_per_doc: maxCharsPerDoc },
      });
      return response?.data || [];
    } catch {
      return [];
    }
  },
};

export interface DocumentContent {
  id: number;
  title: string;
  context?: string | null;
  has_content: boolean;
  text_content?: string | null;
}
